/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from '@ui';
import { translate } from '@utils/i18n';
import ClickAwayListener from 'react-click-away-listener';
import TooltipText from '@components/v2/TooltipText';
import './ComponentTypeSelectorStyles.scss';
import camelToCapitalize from '@utils/functions/camelToCapitalize';
const namespace = 'features.settings.component_types';
function AttachableTypeSelector({ selectedTypes, availableTypes, onChange, onBlur, lockedTypes = [] }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const anchor = useRef(null);
    // Filter out already selected types from the dropdown
    const filteredTypes = availableTypes.filter((option) => !selectedTypes.includes(option.value));
    const labelForType = (type) => { var _a; return ((_a = availableTypes.find((option) => option.value === type)) === null || _a === void 0 ? void 0 : _a.label) || camelToCapitalize(type); };
    const handleAddType = (type) => {
        setDropdownOpen(false);
        onChange([...selectedTypes, type]);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    const handleRemoveType = (type) => {
        if (lockedTypes.includes(type))
            return;
        onChange(selectedTypes.filter((t) => t !== type));
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    return (React.createElement("div", { className: "component-type-selector" },
        React.createElement("div", { className: "component-type-selector__content" },
            selectedTypes.length === 0 && (React.createElement("span", { className: "component-type-selector__empty-state" }, translate('no_attachable_types_selected', { namespace }))),
            selectedTypes.map((type) => (React.createElement(TypeTag, { key: type, type: type, label: labelForType(type), onRemove: () => handleRemoveType(type), removeAllowed: !lockedTypes.includes(type) }))),
            filteredTypes.length > 0 && (React.createElement(Button, { ref: anchor, variant: "secondary", "aria-label": translate('add_attachable_type', { namespace }), onClick: (e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                } },
                React.createElement("i", { className: "fa-solid fa-plus" }))),
            React.createElement(Popup, { anchor: anchor.current, show: dropdownOpen },
                React.createElement(TypeDropdown, { types: filteredTypes, onAddType: handleAddType, onClosePopup: () => setDropdownOpen(false) })))));
}
function TypeDropdown({ onAddType, onClosePopup, types }) {
    const searchRef = useRef(null);
    const [filteredTypes, setFilteredTypes] = useState(types);
    React.useEffect(() => {
        var _a;
        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        setFilteredTypes(types);
    }, [types]);
    const onHandleKeyUp = (event) => {
        const searchValue = event.target.value.toLowerCase();
        setFilteredTypes(types.filter((option) => option.label.toLowerCase().includes(searchValue)));
    };
    return (React.createElement(ClickAwayListener, { onClickAway: onClosePopup },
        React.createElement("div", { className: "component-type-dropdown" },
            React.createElement("div", { className: "qmb-input--blank" },
                React.createElement("input", { ref: searchRef, onKeyUp: onHandleKeyUp, placeholder: translate('search_attachable_types', { namespace }) })),
            React.createElement("div", { className: "component-type-dropdown__list" },
                filteredTypes.length === 0 && (React.createElement("div", { className: "qmb-control qmb-control--disabled" }, translate('no_attachable_types_found', { namespace }))),
                filteredTypes.length > 0 &&
                    filteredTypes.map((option) => (React.createElement(DropdownItem, { key: option.value, type: option.value, label: option.label, onHandleClick: onAddType })))))));
}
function DropdownItem({ type, label, onHandleClick }) {
    return (React.createElement("button", { className: "qmb-control", type: "button", onClick: () => onHandleClick(type) },
        React.createElement("div", { className: "component-type-item" },
            React.createElement("span", { className: "component-type-item__name" }, label))));
}
function TypeTag({ type, label, onRemove, removeAllowed = true }) {
    const removeButton = (React.createElement("button", { className: "qmb-control--icon fa-light fa-xmark", type: "button", "aria-label": `Remove ${type}`, onClick: onRemove, disabled: !removeAllowed }));
    return (React.createElement("span", { className: "qmb-tag--gray" },
        label,
        !removeAllowed ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_remove_attachable_type', { namespace }) }, removeButton)) : (removeButton)));
}
AttachableTypeSelector.defaultProps = {
    onBlur: undefined,
    lockedTypes: []
};
export default AttachableTypeSelector;
